"use client";

import { useMemo, useState, type ReactNode } from "react";
import { EXPERIENCE_SUMMARY_MAX_CHARS } from "@/features/candidate/lib/profile-constants";
import { parseBulletBlocks, truncateSummaryText } from "@/features/candidate/lib/profile-utils";
import { HighlightedText } from "@/features/arbitrator/components/review/highlighted-text";

function renderBulletSummaryBlocks(
  blocks: ReturnType<typeof parseBulletBlocks>,
): ReactNode[] {
  return blocks.map((b, i) => {
    if (b.type === "list" && b.items?.length) {
      return (
        <ul key={i} className="list-disc space-y-1 pl-5 marker:text-foreground/45">
          {b.items.map((item, j) => (
            <li key={j} className="pl-0.5">
              <HighlightedText source="experience">{item}</HighlightedText>
            </li>
          ))}
        </ul>
      );
    }
    if (b.type === "paragraph" && b.text) {
      return (
        <p key={i} className="whitespace-pre-wrap">
          <HighlightedText source="experience">{b.text}</HighlightedText>
        </p>
      );
    }
    return null;
  });
}

export function ExperienceRoleSummary({ summary }: { summary: string }) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncate = summary.length > EXPERIENCE_SUMMARY_MAX_CHARS;
  const displayText =
    expanded || !needsTruncate
      ? summary
      : truncateSummaryText(summary, EXPERIENCE_SUMMARY_MAX_CHARS);

  const blocks = useMemo(() => parseBulletBlocks(displayText), [displayText]);

  return (
    <div className="mt-2 flex w-full flex-col gap-2 text-[13px] font-normal leading-normal tracking-[-0.13px] text-foreground/70">
      {renderBulletSummaryBlocks(blocks)}
      {needsTruncate && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="w-fit py-2 text-left text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:py-0"
        >
          {expanded ? "see less" : "see more"}
        </button>
      )}
    </div>
  );
}
